import React from 'react';
import numbro from 'numbro';
import { InvoiceItemsModel, InvoicePresenter } from '@common/modelDefinition';
import dayjs from 'dayjs';
import useStore from '@data/state/zustand';
import { getGroupedInvoiceItemsByGroup } from '../checkout/CurrentOffer';

interface GroupedInvoiceItems {
    [key: string]: InvoiceItemsModel[];
}

// ============================================================================
// HELPER: Format accommodation item name
// Parsira strukturirani format i vraća prevedeni string
// ============================================================================
const formatAccommodationItemName = (
    itemName: string,
    locale: string,
    translate: (key: string) => string
): React.ReactNode => {
    const parts = itemName.split(':');
    const itemType = parts[0];

    // Helper za formatiranje datuma
    const formatDate = (dateStr: string) => dayjs(dateStr).locale(locale).format('ll');
    
    // Helper za formatiranje date range - VRAĆA NULL AKO NEMA DATUMA
    const formatDateRange = (startIndex: number): string | null => {
        const from = parts[startIndex];
        if (!from) return null;  // NEMA DATUMA - vrati null
        
        const to = parts[startIndex + 1];
        if (to) {
            return `${formatDate(from)} - ${formatDate(to)}`;
        }
        return formatDate(from);
    };

    switch (itemType) {
        case 'CancellationFee':
            return translate('Cancellation fee');

        case 'Accommodation': {
            const dateRange = formatDateRange(1);
            if (!dateRange) return translate(itemName); // Nema datuma - vrati original
            return (
                <>
                    {translate('Accommodation')}: {dateRange}
                </>
            );
        }

        case 'AccommodationAdults': {
            const adultsCount = parts[1];
            const dateRange = formatDateRange(2);
            if (!dateRange) return translate(itemName);
            return (
                <>
                    {translate('Accommodation')} ({adultsCount} {translate('adults')}): {dateRange}
                </>
            );
        }

        case 'AccommodationChild': {
            const ageRange = parts[1];
            const childCount = parts[2];
            const dateRange = formatDateRange(3);
            if (!dateRange) return translate(itemName);
            return (
                <>
                    {translate('Accommodation')} ({translate('child')} {ageRange}{translate('y')}, {childCount}x): {dateRange}
                </>
            );
        }

        case 'AccommodationCapped': {
            const totalGuests = parts[1];
            const dateRange = formatDateRange(2);
            if (!dateRange) return translate(itemName);
            return (
                <>
                    {translate('Accommodation')} ({totalGuests} {translate('guests')}, {translate('capped')}): {dateRange}
                </>
            );
        }

        default:
            return translate(itemName);
    }
};

// ============================================================================
// HELPER: Format tax item name
// ============================================================================
const formatTaxItemName = (
    itemName: string,
    translate: (key: string) => string
): React.ReactNode => {
    const parts = itemName.split(':');
    
    if (parts.length >= 2) {
        return (
            <>
                {translate(parts[0])}: (-{parts[1]}%)
            </>
        );
    }
    
    return translate(itemName);
};

// ============================================================================
// HELPER: Format item name based on type
// ============================================================================
const formatItemName = (
    item: InvoiceItemsModel,
    locale: string,
    translate: (key: string) => string
): React.ReactNode => {
    if (item.type === 'accommodation') {
        return formatAccommodationItemName(item.itemName, locale, translate);
    }
    
    if (item.type === 'tax') {
        return formatTaxItemName(item.itemName, translate);
    }
    
    // Default - vraća originalni itemName
    return translate(item.itemName);
};

// ============================================================================
// INVOICE HEADER COMPONENT
// ============================================================================

//ako budemo dodavali popust na pojedinačne stavke ovdje, onda ćeš samo gledati u status invoiceCovera NIŠTA DRUGOOOOO!!!!
const InvoiceHeader = (props: InvoicePresenter) => {
    const { invoiceItems, invoice, translate, print = false } = props;
    const { exchangeRates, currency, locale } = invoice;

    const padding = useStore((appState: any) => appState.state.invoicePresenter.padding);

    const exchangeRatesWhenInvoiceIssued = exchangeRates ? JSON.parse(exchangeRates) : {};
    const currencyExchangeRate: number = exchangeRatesWhenInvoiceIssued[currency]
        ? Number(exchangeRatesWhenInvoiceIssued[currency])
        : 1;

    const groupedInvoiceItems: GroupedInvoiceItems = getGroupedInvoiceItemsByGroup(invoiceItems);
    const itemsWithTaxPercentage2: InvoiceItemsModel[] = invoiceItems.filter((i) => Number(i.taxPercentage2) > 0);

    if (Object.keys(groupedInvoiceItems).length === 0) {
        if (!invoice.id) {
            return (
                <React.Fragment>
                    <InvoiceItemsHeader {...props} />
                    <ul
                        className={print ? 'mt-2 border-top' : 'list-group list-group-flush'}
                        style={print ? { listStyle: 'none' } : {}}
                    >
                        <li className={print ? 'p-3' : 'list-group-item p-3'}></li>
                        <li className={print ? 'p-3' : 'list-group-item p-3'}></li>
                        <li className={print ? 'p-3' : 'list-group-item p-3'}></li>
                    </ul>
                </React.Fragment>
            );
        } else {
            return null;
        }
    }

    return (
        <div className="">
            <InvoiceItemsHeader {...props} />

            {Object.keys(groupedInvoiceItems).map((group: string, key: number) => {
                return (
                    <ul
                        key={key}
                        className={print ? 'mt-0' : 'list-group list-group-flush mt-0'}
                        style={print ? { listStyle: 'none' } : {}}
                    >
                        {group !== 'null' && (
                            <li className={print ? `pt-1 pb-1 bg-light` : 'list-group-item p-1 bg-light'}>
                                <small>
                                    <div className="text-left">
                                        <b>
                                            {group.includes('Room')
                                                ? group.split(':').map((text: string) => {
                                                      if (text === 'Room') {
                                                          return translate(text);
                                                      } else {
                                                          return `: ${text}`;
                                                      }
                                                  })
                                                : group}
                                        </b>
                                    </div>
                                </small>
                            </li>
                        )}
                        {groupedInvoiceItems[group].map((i, index) => {
                            const taxgroup =
                                Number(i.taxPercentage1) === 0 && Number(i.taxPercentage2) === 0
                                    ? 0
                                    : Number(i.taxPercentage1) === 0 && Number(i.taxPercentage2) > 0
                                    ? Number(i.taxPercentage2)
                                    : Number(i.taxPercentage1) > 0 && Number(i.taxPercentage2) === 0
                                    ? Number(i.taxPercentage1)
                                    : `Preračunata stopa`;
                            return (
                                <li
                                    key={index}
                                    className={print ? `pb-${padding} pt-${padding}` : 'list-group-item p-1 '}
                                >
                                    <small>
                                        <div className="row d-flex justify-content-between">
                                            <div className="col-4 col-sm-3">
                                                {formatItemName(i, locale, translate)}
                                            </div>
                                            <div className="col-4 col-sm-1">
                                                {numbro(i.quantity).format({ mantissa: 2, trimMantissa: true })}
                                            </div>

                                            <div className="col-4 col-sm-1 text-center">
                                                {i.nettoUnitPrice !== null
                                                    ? numbro(i.nettoUnitPrice * Number(currencyExchangeRate)).format({
                                                          mantissa: 2,
                                                          trimMantissa: false,
                                                          thousandSeparated: true,
                                                          //negative: 'parenthesis',
                                                      })
                                                    : 0}
                                            </div>
                                            <div className="col-4 col-sm-1 text-center">
                                                {i.bruttoUnitPrice !== null
                                                    ? numbro(i.bruttoUnitPrice * Number(currencyExchangeRate)).format({
                                                          mantissa: 2,
                                                          trimMantissa: false,
                                                          thousandSeparated: true,
                                                          // negative: 'parenthesis',
                                                      })
                                                    : 0}
                                            </div>
                                            {/*trenutnno podržavamo samo jednu poreznu grupu, ali ako bude trebalo više, spremni smo*/}
                                            <div className="col-4 col-sm-1 text-center">
                                                {Number(i.taxPercentage1) > 0 && Number(i.taxPercentage2) > 0
                                                    ? taxgroup
                                                    : numbro(taxgroup).format({
                                                          mantissa: 2,
                                                          trimMantissa: true,
                                                      })}
                                                {Number(i.taxPercentage1) > 0 && Number(i.taxPercentage2) > 0
                                                    ? ''
                                                    : '%'}
                                            </div>
                                            <div className="col-4 col-sm-1 text-right">
                                                {i.tax1ValueWithDiscount !== null
                                                    ? numbro(
                                                          i.tax1ValueWithDiscount * Number(currencyExchangeRate)
                                                      ).format({
                                                          mantissa: 2,
                                                          trimMantissa: false,
                                                          thousandSeparated: true,
                                                          //  negative: 'parenthesis',
                                                      })
                                                    : 0}
                                            </div>

                                            {itemsWithTaxPercentage2.length > 0 ? (
                                                <div className="col-4 col-sm-1 text-right">
                                                    {i.tax2ValueWithDiscount !== null
                                                        ? numbro(
                                                              i.tax2ValueWithDiscount * Number(currencyExchangeRate)
                                                          ).format({
                                                              mantissa: 2,
                                                              trimMantissa: false,
                                                              thousandSeparated: true,
                                                              //  negative: 'parenthesis',
                                                          })
                                                        : 0}
                                                </div>
                                            ) : null}
                                            <div className="col-4 col-sm-1 text-right">
                                                {numbro(i.discountPercentage ? i.discountPercentage : 0).format({
                                                    mantissa: 2,
                                                    trimMantissa: true,
                                                })}
                                                %
                                            </div>
                                            <div className="col-4 col-sm-1 text-right">
                                                {i.bruttoValueWithDiscount !== null
                                                    ? numbro(
                                                          i.bruttoValueWithDiscount * Number(currencyExchangeRate)
                                                      ).format({
                                                          mantissa: 2,
                                                          trimMantissa: false,
                                                          thousandSeparated: true,
                                                      })
                                                    : 0}
                                            </div>
                                        </div>
                                    </small>
                                </li>
                            );
                        })}
                    </ul>
                );
            })}
            <hr />
        </div>
    );
};

export const InvoiceItemsHeader = (props: InvoicePresenter) => {
    const { translate, invoiceItems, print } = props;

    const itemsWithTaxPercentage2: InvoiceItemsModel[] = invoiceItems.filter((i) => Number(i.taxPercentage2) > 0);
    return (
        <div className="">
            <ul
                className={print ? 'mt-2 mb-1 pt-3 border-top' : 'list-group list-group-flush mt-2 border-top'}
                style={print ? { listStyle: 'none' } : {}}
            >
                <li className={print ? 'p-0' : 'list-group-item p-0'}>
                    <div className="row d-flex justify-content-between">
                        <div className="col-4 col-sm-3">
                            <b> {translate('Name')} </b>
                        </div>
                        <div className="col-4 col-sm-1">
                            <b> {translate('Quantity')} </b>
                        </div>
                        <div className="col-4 col-sm-1 text-center">
                            <b> {translate('Unit price')} </b>
                        </div>
                        <div className="col-4 col-sm-1 text-center">
                            <b> {translate('Retail price')} </b>
                        </div>
                        <div className="col-4 col-sm-1 text-center">
                            <b> {translate('Tax rate')} </b>
                        </div>
                        <div className="col-4 col-sm-1 text-right">
                            <b> {translate('Tax')} </b>
                        </div>
                        {itemsWithTaxPercentage2.length > 0 ? (
                            <div className="col-4 col-sm-1 text-right">
                                <b> {translate('Tax2')}</b>
                            </div>
                        ) : null}
                        <div className="col-4 col-sm-1 text-right">
                            <b> {translate('Discount')} </b>
                        </div>
                        <div className="col-4 col-sm-1 text-right">
                            <b> {translate('Amount')} </b>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    );
};

export default InvoiceHeader;